import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "@arco-design/web-react";
import {
  createShop,
  deleteShop,
  getWebhookUrl,
  listShops,
  refreshShopToken,
  testShopConnection,
  updateShop,
  type ShopSummary,
  type UpdateShopInput,
} from "../api/shops.js";
import { getCatalog, type PlatformManifest } from "../api/platforms.js";
import { CopyableText } from "../components/CopyableText.js";
import { parseApiError } from "../api/http.js";

const { TabPane } = Tabs;
const { Title, Text } = Typography;

interface ShopFormState {
  shop_id: string;
  shop_name: string;
  app_secret: string;
  extra: Record<string, string>;
}

export function ShopsPage(): JSX.Element {
  const [catalog, setCatalog] = useState<PlatformManifest[]>([]);
  const [shops, setShops] = useState<ShopSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState<ShopFormState>({
    shop_id: "",
    shop_name: "",
    app_secret: "",
    extra: {},
  });
  const [webhookInfo, setWebhookInfo] = useState<{ url: string | null; path: string } | null>(null);
  const [editingShop, setEditingShop] = useState<ShopSummary | null>(null);
  const [editFormState, setEditFormState] = useState<{
    shop_name: string;
    app_secret: string;
    extra: Record<string, string>;
    expires_at: string; // "YYYY-MM-DD" 或 ""
  }>({ shop_name: "", app_secret: "", extra: {}, expires_at: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);

  const refresh = (): void => {
    setLoading(true);
    Promise.all([getCatalog(), listShops()])
      .then(([cat, list]) => {
        setCatalog(cat.platforms);
        setShops(list);
        if (!activeTab && cat.platforms[0]) setActiveTab(cat.platforms[0].platform);
      })
      .catch((err: unknown) => Message.error(parseApiError(err).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const platformDef = useMemo(
    () => catalog.find((p) => p.platform === activeTab) ?? null,
    [catalog, activeTab],
  );
  const platformShops = useMemo(
    () => shops.filter((s) => s.platform === activeTab),
    [shops, activeTab],
  );
  const editPlatformDef = useMemo(
    () => (editingShop ? catalog.find((p) => p.platform === editingShop.platform) ?? null : null),
    [catalog, editingShop],
  );

  const handleAdd = (): void => {
    setFormState({ shop_id: "", shop_name: "", app_secret: "", extra: {} });
    setModalOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!platformDef) return;
    if (!formState.shop_id || !formState.shop_name || !formState.app_secret) {
      Message.warning("shop_id / shop_name / app_secret 都必填");
      return;
    }
    setSubmitting(true);
    try {
      await createShop({
        platform: platformDef.platform,
        shop_id: formState.shop_id,
        shop_name: formState.shop_name,
        app_secret: formState.app_secret,
        extra: formState.extra,
      });
      Message.success("店铺已添加");
      setModalOpen(false);
      refresh();
    } catch (err) {
      Message.error(parseApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (shop_pk: string): Promise<void> => {
    try {
      await deleteShop(shop_pk);
      Message.success("已删除");
      refresh();
    } catch (err) {
      Message.error(parseApiError(err).message);
    }
  };

  const handleTest = async (shop_pk: string): Promise<void> => {
    try {
      const res = await testShopConnection(shop_pk);
      if (res.ok) {
        Message.success(`连通正常，最近一小时查到 ${res.count} 条订单`);
      } else {
        Message.error(`连通失败：${res.reason}`);
      }
    } catch (err) {
      Message.error(parseApiError(err).message);
    }
  };

  const handleRefreshToken = async (shop_pk: string): Promise<void> => {
    try {
      const res = await refreshShopToken(shop_pk);
      if (res.ok) {
        const expiresStr = res.expires_at
          ? new Date(res.expires_at).toLocaleDateString("zh-CN")
          : "未知";
        Message.success(`授权已刷新，新到期时间：${expiresStr}`);
        refresh();
      } else {
        Message.error(`刷新失败：${res.reason}`);
      }
    } catch (err) {
      Message.error(parseApiError(err).message);
    }
  };

  const handleShowWebhook = async (shop_pk: string): Promise<void> => {
    try {
      const info = await getWebhookUrl(shop_pk);
      setWebhookInfo(info);
    } catch (err) {
      Message.error(parseApiError(err).message);
    }
  };

  const handleEdit = (shop: ShopSummary): void => {
    setEditingShop(shop);
    const expiresIso = shop.access_token_expires_at
      ? new Date(shop.access_token_expires_at).toISOString().slice(0, 10)
      : "";
    setEditFormState({ shop_name: shop.shop_name, app_secret: "", extra: {}, expires_at: expiresIso });
  };

  const handleEditSubmit = async (): Promise<void> => {
    if (!editingShop) return;
    const update: UpdateShopInput = {};
    if (editFormState.shop_name && editFormState.shop_name !== editingShop.shop_name) {
      update.shop_name = editFormState.shop_name;
    }
    if (editFormState.app_secret) update.app_secret = editFormState.app_secret;
    // 到期时间：有值时发 ISO 字符串，显式清空时发 null（对比原始值）
    const origExpires = editingShop.access_token_expires_at
      ? new Date(editingShop.access_token_expires_at).toISOString().slice(0, 10)
      : "";
    if (editFormState.expires_at !== origExpires) {
      update.access_token_expires_at = editFormState.expires_at
        ? new Date(editFormState.expires_at).toISOString()
        : null;
    }
    const nonEmptyExtra = Object.fromEntries(
      Object.entries(editFormState.extra).filter(([, v]) => v.length > 0),
    );
    if (Object.keys(nonEmptyExtra).length > 0) update.extra = nonEmptyExtra;

    if (Object.keys(update).length === 0) {
      Message.info("没有修改任何内容");
      setEditingShop(null);
      return;
    }
    setEditSubmitting(true);
    try {
      await updateShop(editingShop.id, update);
      Message.success("店铺已更新");
      setEditingShop(null);
      refresh();
    } catch (err) {
      Message.error(parseApiError(err).message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const renderExpiryBadge = (ts: number | string | null): JSX.Element => {
    if (!ts) return <Tag color="gray">未填写</Tag>;
    // 服务器返回的 Date 字段被 JSON 序列化成 ISO 字符串，前端要兼容 number / string 两种
    const tsMs = typeof ts === "string" ? new Date(ts).getTime() : ts;
    if (!Number.isFinite(tsMs)) return <Tag color="gray">日期格式错误</Tag>;
    const diffDays = Math.floor((tsMs - Date.now()) / (1000 * 60 * 60 * 24));
    const dateStr = new Date(tsMs).toLocaleDateString("zh-CN");
    if (diffDays < 0) return <Tag color="red">已过期 ({dateStr})</Tag>;
    if (diffDays <= 3) return <Tag color="orangered">{diffDays}天后到期</Tag>;
    if (diffDays <= 7) return <Tag color="orange">{diffDays}天后到期</Tag>;
    return <Tag color="green">{diffDays}天后到期</Tag>;
  };

  return (
    <div className="os-stack">
      <div className="os-row" style={{ justifyContent: "space-between" }}>
        <Title heading={5} style={{ margin: 0 }}>
          店铺与凭证
        </Title>
        <Button type="primary" disabled={!platformDef} onClick={handleAdd}>
          + 添加 {platformDef?.label ?? ""} 店铺
        </Button>
      </div>

      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        {catalog.map((p) => (
          <TabPane key={p.platform} title={p.label}>
            <div className="os-stack">
              {p.setup_hints.length > 0 && (
                <Alert
                  type="warning"
                  content={
                    <ul style={{ margin: 0, paddingInlineStart: 16 }}>
                      {p.setup_hints.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  }
                />
              )}
              {platformShops.length === 0 ? (
                <Empty description="还没有该平台的店铺。点击右上角添加。" />
              ) : (
                <Table
                  data={platformShops}
                  loading={loading}
                  rowKey="id"
                  pagination={false}
                  columns={[
                    { title: "店铺名", dataIndex: "shop_name" },
                    { title: "shop_id", dataIndex: "shop_id" },
                    {
                      title: "状态",
                      dataIndex: "enabled",
                      render: (v: boolean) =>
                        v ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>,
                    },
                    {
                      title: "凭证摘要",
                      dataIndex: "app_secret_masked",
                      render: (m: string | null) => m ?? <Text type="secondary">未设置</Text>,
                    },
                    {
                      title: "授权到期",
                      dataIndex: "access_token_expires_at",
                      render: (ts: number | string | null) => renderExpiryBadge(ts),
                    },
                    {
                      title: "操作",
                      render: (_: unknown, row: ShopSummary) => (
                        <Space>
                          <Button size="mini" onClick={() => handleEdit(row)}>
                            编辑
                          </Button>
                          {row.can_refresh && (
                            <Button size="mini" type="outline" onClick={() => handleRefreshToken(row.id)}>
                              刷新授权
                            </Button>
                          )}
                          <Button size="mini" onClick={() => handleTest(row.id)}>
                            测试连通
                          </Button>
                          {p.webhook_supported && (
                            <Button size="mini" onClick={() => handleShowWebhook(row.id)}>
                              Webhook 地址
                            </Button>
                          )}
                          <Popconfirm
                            title="确定删除此店铺？"
                            onOk={() => handleDelete(row.id)}
                          >
                            <Button size="mini" status="danger">
                              删除
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          </TabPane>
        ))}
      </Tabs>

      <Modal
        title={`添加 ${platformDef?.label ?? ""} 店铺`}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        unmountOnExit
      >
        {platformDef && (
          <Form layout="vertical">
            {platformDef.required_fields.map((f) => (
              <Form.Item
                key={f.key}
                label={f.label}
                extra={f.description ? <Text type="secondary">{f.description}</Text> : undefined}
              >
                {f.scope === "shop_id" ? (
                  <Input
                    value={formState.shop_id}
                    onChange={(v) => setFormState({ ...formState, shop_id: v })}
                  />
                ) : f.scope === "app_secret" ? (
                  f.type === "password" ? (
                    <Input.Password
                      value={formState.app_secret}
                      onChange={(v) => setFormState({ ...formState, app_secret: v })}
                    />
                  ) : (
                    <Input
                      value={formState.app_secret}
                      onChange={(v) => setFormState({ ...formState, app_secret: v })}
                    />
                  )
                ) : f.type === "password" ? (
                  <Input.Password
                    value={formState.extra[f.key] ?? ""}
                    onChange={(v) => setFormState({ ...formState, extra: { ...formState.extra, [f.key]: v } })}
                  />
                ) : (
                  <Input
                    value={formState.extra[f.key] ?? ""}
                    onChange={(v) => setFormState({ ...formState, extra: { ...formState.extra, [f.key]: v } })}
                  />
                )}
              </Form.Item>
            ))}
            <Form.Item label="显示名（你给这个店铺取的别名）">
              <Input
                value={formState.shop_name}
                onChange={(v) => setFormState({ ...formState, shop_name: v })}
                placeholder="例如：旗舰店"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Webhook 地址"
        visible={webhookInfo !== null}
        onCancel={() => setWebhookInfo(null)}
        footer={null}
        unmountOnExit
      >
        {webhookInfo && (
          <div className="os-stack">
            <Text>
              请把下面地址填到对应平台开放后台的「消息推送」配置里：
            </Text>
            <CopyableText
              value={webhookInfo.url ?? `<请将 PUBLIC_BASE_URL env 配好后回此重新查看>${webhookInfo.path}`}
            />
            <Text type="secondary">
              路径相对地址：<code>{webhookInfo.path}</code>
            </Text>
          </div>
        )}
      </Modal>

      <Modal
        title={`编辑店铺 — ${editingShop?.shop_name ?? ""}`}
        visible={editingShop !== null}
        onCancel={() => setEditingShop(null)}
        onOk={handleEditSubmit}
        confirmLoading={editSubmitting}
        unmountOnExit
      >
        {editingShop && editPlatformDef && (
          <Form layout="vertical">
            <Form.Item label="shop_id">
              <Input value={editingShop.shop_id} disabled />
            </Form.Item>
            <Form.Item label="显示名">
              <Input
                value={editFormState.shop_name}
                onChange={(v) => setEditFormState({ ...editFormState, shop_name: v })}
              />
            </Form.Item>
            {editPlatformDef.required_fields
              .filter((f) => f.scope === "app_secret")
              .map((f) => (
                <Form.Item
                  key={f.key}
                  label={f.label}
                  extra={<Text type="secondary">留空则不修改</Text>}
                >
                  <Input.Password
                    value={editFormState.app_secret}
                    onChange={(v) => setEditFormState({ ...editFormState, app_secret: v })}
                    placeholder="留空则不修改"
                  />
                </Form.Item>
              ))}
            {editPlatformDef.required_fields
              .filter((f) => f.scope === "extra")
              .map((f) => (
                <Form.Item
                  key={f.key}
                  label={f.label}
                  extra={
                    <Text type="secondary">
                      {f.description ? `${f.description}　` : ""}留空则不修改
                    </Text>
                  }
                >
                  {f.type === "password" ? (
                    <Input.Password
                      value={editFormState.extra[f.key] ?? ""}
                      onChange={(v) =>
                        setEditFormState({ ...editFormState, extra: { ...editFormState.extra, [f.key]: v } })
                      }
                      placeholder="留空则不修改"
                    />
                  ) : (
                    <Input
                      value={editFormState.extra[f.key] ?? ""}
                      onChange={(v) =>
                        setEditFormState({ ...editFormState, extra: { ...editFormState.extra, [f.key]: v } })
                      }
                      placeholder="留空则不修改"
                    />
                  )}
                </Form.Item>
              ))}
            <Form.Item
              label="accessToken 到期日"
              extra={<Text type="secondary">填写后可触发到期提醒和自动续期（留空则不设置）</Text>}
            >
              <Input
                type="date"
                value={editFormState.expires_at}
                onChange={(v) => setEditFormState({ ...editFormState, expires_at: v })}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}

// Select 占位，避免未引用警告（动态字段未来可能用到下拉）
void Select;
